/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * X-3 (S711 review) — ONE owner of the payment library, and it can try again.
 *
 * The library was loaded at module scope in four files, each with
 * `const stripePromise = loadStripe(key)`. A settled promise does not re-run:
 * if the network was down when the module was first imported, or the script
 * request failed during an outage, the FAILURE was what every later render saw
 * for the life of the page. The billing form is the cart's own content, so a
 * provider that never resolved took the form -- and everything inside it --
 * off the screen: the blank cart after reconnecting.
 *
 * Loading once, outside render, is still right and is kept: this module owns
 * the promise, hands the same one to everybody, and forgets it only when it
 * failed, so the next request tries again. (`@stripe/stripe-js` itself resets
 * its script loader after a failure, so asking again genuinely retries.)
 *
 * `<Elements>` refuses a changed `stripe` prop, so a retried load comes with a
 * new GENERATION number, which the providers use as a React key to remount.
 */

import { loadStripe, type Stripe } from "@stripe/stripe-js";
import configuration from "@/config/configuration";

let current: Promise<Stripe | null> | null = null;
let generation = 0;
const listeners = new Set<() => void>();

/** The shared payment-library promise, requesting it if there is none. */
export function getStripePromise(): Promise<Stripe | null> {
  if (!current) {
    generation += 1;
    const attempt: Promise<Stripe | null> = loadStripe(
      configuration.stripe.publishable_key,
    ).then(
      (stripe) => {
        // `null` means the library could not be initialised. Forget it, so
        // the next request tries again rather than being handed the failure.
        if (!stripe && current === attempt) current = null;
        return stripe;
      },
      (error: unknown) => {
        if (current === attempt) current = null;
        throw error;
      },
    );
    // The consumers await it; this only stops a failed load being reported
    // as an unhandled rejection on top of the message the page shows.
    attempt.catch(() => undefined);
    current = attempt;
  }
  return current;
}

/** Which load the current promise belongs to. Changes only on a retry. */
export function getStripeGeneration(): number {
  return generation;
}

/**
 * Retry the load if -- and only if -- the last one failed. A load that
 * succeeded is kept: re-creating a working payment context would throw away
 * whatever the customer had typed into it. Returns whether a retry started.
 */
export function retryStripeIfFailed(): boolean {
  if (current) return false;
  getStripePromise();
  for (const listener of Array.from(listeners)) listener();
  return true;
}

/** Be told when a retried load has replaced the promise. */
export function subscribeStripeLoader(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test seam: forget everything, as a fresh page would. */
export function resetStripeLoaderForTests(): void {
  current = null;
  generation = 0;
  listeners.clear();
}
